import React, { useState } from "react";
import { MenuItem, Select, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import DarkModeRounded from "@mui/icons-material/DarkModeRounded";
import DesktopWindowsRounded from "@mui/icons-material/DesktopWindowsRounded";
import LightModeRounded from "@mui/icons-material/LightModeRounded";
import { useColorMode } from "context/ColorMode";
import { LANGUAGES, languagePreference, setLanguage } from "i18n";
import { SettingsRow, SettingsSection } from "./SettingsSection";

const THEME_CHOICES = [
  { value: "system", icon: <DesktopWindowsRounded fontSize="small" /> },
  { value: "light", icon: <LightModeRounded fontSize="small" /> },
  { value: "dark", icon: <DarkModeRounded fontSize="small" /> },
];

/**
 * Appearance and Language: the settings that belong to the device rather than the
 * account. Part of Settings, and all of it for someone not signed in (PublicSettingsButton).
 */
const DisplaySettings = () => {
  const { t } = useTranslation();
  const { preference, setPreference } = useColorMode();
  // "auto" or a language code; the i18n language alone cannot tell System from a pick.
  const [languageChoice, setLanguageChoice] = useState(languagePreference);

  return (
    <>
      <SettingsSection title={t("settings.appearance.title")}>
        <SettingsRow
          label={t("settings.appearance.theme")}
          action={
            <ToggleButtonGroup
              exclusive
              size="small"
              value={preference}
              onChange={(e, next) => next && setPreference(next)}
              aria-label={t("settings.appearance.theme")}
            >
              {THEME_CHOICES.map(({ value, icon }) => (
                <Tooltip key={value} title={t(`settings.appearance.${value}`)}>
                  <ToggleButton value={value} aria-label={t(`settings.appearance.${value}`)}>
                    {icon}
                  </ToggleButton>
                </Tooltip>
              ))}
            </ToggleButtonGroup>
          }
        />
      </SettingsSection>

      <SettingsSection title={t("settings.language.title")}>
        <SettingsRow
          label={t("settings.language.label")}
          detail={t("settings.language.detail")}
          action={
            <Select
              size="small"
              value={languageChoice}
              onChange={(e) => {
                setLanguage(e.target.value);
                setLanguageChoice(e.target.value);
              }}
              inputProps={{ "aria-label": t("settings.language.label") }}
            >
              {/* Follows the browser's language, the default until one is picked. */}
              <MenuItem value="auto">{t("settings.language.auto")}</MenuItem>
              {/* Each language named in itself, so it can be found from any other. */}
              {LANGUAGES.map(({ code, label }) => (
                <MenuItem key={code} value={code} lang={code}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          }
        />
      </SettingsSection>
    </>
  );
};

export default DisplaySettings;
